import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobalInvestment } from '../../types/ScenarioManagement';
import { Investment } from '../../types/Investment';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/useToast';
import { JSONPersistenceService } from '../../services/JSONPersistenceService';
import { InvestmentForm } from './InvestmentForm';
import { StandardButton } from '../ui';

type ViewMode = 'list' | 'create' | 'edit';

const GlobalInvestmentManager: React.FC = () => {
  const { isDark } = useTheme();
  const { showToast } = useToast();
  
  const [globalInvestments, setGlobalInvestments] = useState<GlobalInvestment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState<ViewMode>('list');
  const [selectedInvestment, setSelectedInvestment] = useState<Investment | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    loadGlobalInvestments();
  }, []);

  const loadGlobalInvestments = async () => {
    try {
      setLoading(true);
      const persistence = JSONPersistenceService.getInstance();
      let data = await persistence.loadData();
      
      // If no data exists, initialize with defaults
      if (!data) {
        data = await persistence.initializeWithDefaults();
      }
      
      if (data?.globalInvestments) {
        const investments = data.globalInvestments.map(([id, investment]) => investment);
        setGlobalInvestments(investments);
        console.log('Loaded global investments:', investments.length);
      } else {
        // If globalInvestments is missing, initialize from data file
        const { investments } = await import('../../data/investments');
        const globalInvestmentsData: GlobalInvestment[] = investments.map(inv => ({
          ...inv,
          businessOutcomes: inv.businessOutcomes || [],
          isGlobal: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: 'System',
          isEnabled: true
        }));
        
        // Save to persistence
        data.globalInvestments = globalInvestmentsData.map(inv => [inv.id, inv]);
        await persistence.saveData(data);
        
        setGlobalInvestments(globalInvestmentsData);
        console.log('Initialized global investments from data file:', globalInvestmentsData.length);
      }
    } catch (err) {
      console.error('Failed to load global investments:', err);
      setError('Failed to load investments');
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalInvestments = async (investments: GlobalInvestment[]) => {
    try {
      const persistence = JSONPersistenceService.getInstance();
      const data = await persistence.loadData();
      
      if (data) {
        data.globalInvestments = investments.map(inv => [inv.id, inv]);
        await persistence.saveData(data);
      }
    } catch (err) {
      console.error('Failed to save global investments:', err);
      throw new Error('Failed to save investments');
    }
  };

  const handleCreate = () => {
    setSelectedInvestment(null);
    setCurrentMode('create');
  };

  const handleEdit = (investment: GlobalInvestment) => {
    // Convert GlobalInvestment to Investment for the form
    const investmentForForm: Investment = {
      id: investment.id,
      name: investment.name,
      icon: investment.icon,
      description: investment.description,
      cost: investment.cost,
      impact: investment.impact,
      businessOutcomes: investment.businessOutcomes,
      category: investment.category as Investment['category']
    };
    setSelectedInvestment(investmentForForm);
    setCurrentMode('edit');
  };

  const handleSave = async (investment: Investment) => {
    try {
      const now = new Date();
      let updatedInvestments: GlobalInvestment[];

      if (currentMode === 'create') {
        const newGlobalInvestment: GlobalInvestment = {
          ...investment,
          businessOutcomes: investment.businessOutcomes || [],
          isGlobal: true,
          isEnabled: true,
          createdAt: now,
          updatedAt: now,
          createdBy: 'Admin'
        };
        updatedInvestments = [...globalInvestments, newGlobalInvestment];
        
        showToast({
          title: 'Investment Created',
          message: `${investment.name} has been added to the investment library`,
          type: 'success'
        });
      } else {
        updatedInvestments = globalInvestments.map(inv =>
          inv.id === investment.id
            ? {
                ...inv,
                ...investment,
                businessOutcomes: investment.businessOutcomes || [],
                createdAt: inv.createdAt instanceof Date ? inv.createdAt : new Date(inv.createdAt),
                updatedAt: now,
                lastModifiedBy: 'Admin'
              } as GlobalInvestment
            : inv
        );
        
        showToast({
          title: 'Investment Updated',
          message: `Changes to ${investment.name} have been saved`,
          type: 'success'
        });
      }

      await saveGlobalInvestments(updatedInvestments);
      setGlobalInvestments(updatedInvestments);
      setCurrentMode('list');
      setSelectedInvestment(null);
    } catch (error) {
      showToast({
        title: 'Error',
        message: 'Failed to save investment',
        type: 'error'
      });
    }
  };


  const handleDelete = async (investmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this investment? This action cannot be undone.')) {
      return;
    }

    try {
      const updatedInvestments = globalInvestments.filter(inv => inv.id !== investmentId);
      await saveGlobalInvestments(updatedInvestments);
      setGlobalInvestments(updatedInvestments);
      
      showToast({
        title: 'Investment Deleted',
        message: 'Investment has been removed from the investment library',
        type: 'success'
      });
    } catch (error) {
      showToast({
        title: 'Error',
        message: 'Failed to delete investment',
        type: 'error'
      });
    }
  };

  const handleCancel = () => {
    setCurrentMode('list');
    setSelectedInvestment(null);
  };

  const filteredInvestments = globalInvestments.filter(investment => {
    const matchesSearch = investment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         investment.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || investment.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(new Set(globalInvestments.map(inv => inv.category)));

  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'education': return '🎓';
      case 'infrastructure': return '🏗️';
      case 'technology': return '💻';
      case 'defense': return '🛡️';
      default: return '📦';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-professional-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <StandardButton
          onClick={loadGlobalInvestments}
          variant="primary"
        >
          Retry
        </StandardButton>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Investment Library
          </h2>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Manage investments available across all scenarios ({globalInvestments.length} total)
          </p>
        </div>

        <StandardButton
          onClick={handleCreate}
          variant="primary"
          leftIcon="➕"
        >
          Create Investment
        </StandardButton>
      </div>

      <AnimatePresence mode="wait">
        {currentMode === 'list' && (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search investments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'border-white/20 bg-white/5 text-white placeholder-slate-400' 
                      : 'border-slate-300 bg-white text-slate-900 placeholder-slate-500'
                  } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={`px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'border-white/20 bg-white/5 text-white' 
                    : 'border-slate-300 bg-white text-slate-900'
                } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {getCategoryIcon(category)} {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Investment Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredInvestments.map((investment) => (
                <motion.div
                  key={investment.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`relative p-4 rounded-lg border transition-all duration-200 ${
                    investment.isEnabled
                      ? isDark
                        ? 'bg-white/5 border-white/10 hover:border-white/20'
                        : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'
                      : 'opacity-50 bg-slate-100 border-slate-200'
                  }`}
                >
                  {/* Actions Menu */}
                  <div className="absolute top-4 right-4 flex gap-1">
                    <StandardButton
                      onClick={() => handleEdit(investment)}
                      variant="ghost"
                      size="small"
                      className="!p-1.5"
                      title="Edit investment"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                      </svg>
                    </StandardButton>
                    <StandardButton
                      onClick={() => handleDelete(investment.id)}
                      variant="ghost"
                      size="small"
                      className="!p-1.5 !text-red-400 hover:!text-red-300"
                      title="Delete investment"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                      </svg>
                    </StandardButton>
                  </div>

                  <div className="mb-3">
                    <div className="text-2xl">{investment.icon}</div>
                  </div>

                  <h3 className={`font-semibold mb-2 ${
                    isDark ? 'text-white' : 'text-slate-800'
                  }`}>
                    {investment.name}
                  </h3>

                  <p className={`text-sm mb-3 ${
                    isDark ? 'text-slate-300' : 'text-slate-600'
                  }`}>
                    {investment.description}
                  </p>

                  {/* Business Outcomes Tags */}
                  {investment.businessOutcomes && investment.businessOutcomes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {investment.businessOutcomes.slice(0, 4).map((outcome, index) => (
                        <span
                          key={index}
                          className={`text-xs px-2 py-1 rounded-full ${
                            isDark
                              ? 'bg-professional-blue-900/30 text-professional-blue-300 border border-professional-blue-700'
                              : 'bg-blue-50 text-blue-700 border border-blue-200'
                          }`}
                        >
                          {outcome}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${
                      isDark ? 'text-slate-200' : 'text-slate-700'
                    }`}>
                      ${(investment.cost / 1000).toFixed(0)}K
                    </span>
                  </div>

                </motion.div>
              ))}
            </div>

            {filteredInvestments.length === 0 && (
              <div className="text-center py-12">
                <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  No investments found matching your criteria
                </p>
              </div>
            )}
          </motion.div>
        )}

        {(currentMode === 'create' || currentMode === 'edit') && (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <InvestmentForm
              investment={selectedInvestment || undefined}
              onSave={handleSave}
              onCancel={handleCancel}
              mode={currentMode}
              scenarioMode={false}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GlobalInvestmentManager;